interface InstagramProfileFields {
  isBusinessAccount?: boolean;
  businessCategory?: string | null;
}

interface BrandLeadInput {
  sourceUrl?: string | null;
  customFields?: Record<string, unknown> | null;
}

type InstagramProfileKind = 'brand' | 'person';

const ABSENT_CATEGORY_PLACEHOLDERS = new Set(['', 'none', 'null', 'undefined']);

function sanitizeBusinessCategory(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return ABSENT_CATEGORY_PLACEHOLDERS.has(trimmed.toLowerCase()) ? undefined : trimmed;
}

function isInstagram(lead: BrandLeadInput): boolean {
  return lead.sourceUrl?.includes('instagram.com') ?? false;
}

function fields(lead: BrandLeadInput): Record<string, unknown> {
  return (lead.customFields ?? {}) as Record<string, unknown>;
}

/**
 * How the enrichment pipeline classified this Instagram account. Undefined for a
 * lead enriched before FLOSEAI-625 shipped, which is why the callers below fall
 * back to the legacy signal rather than treating undefined as "person".
 */
export function getInstagramProfileKind(
  lead: BrandLeadInput | null | undefined,
): InstagramProfileKind | undefined {
  if (!lead || !isInstagram(lead)) return undefined;
  const kind = fields(lead).profileKind;
  return kind === 'brand' || kind === 'person' ? kind : undefined;
}

/**
 * A BUSINESS Instagram account, evaluated as a company/brand.
 *
 * Instagram used to be a brand-only channel in this product (FLOSEAI-142), so
 * any enriched Instagram lead counted. FLOSEAI-625 splits it: business accounts
 * stay brands, personal accounts get an interests analysis instead. The legacy
 * `instagramProfile` fallback only applies when `profileKind` is ABSENT —
 * person leads carry that payload too, so checking it first would frame a human
 * being as a company.
 */
export function isBrandLead(lead: BrandLeadInput | null | undefined): boolean {
  if (!lead || !isInstagram(lead)) return false;

  const kind = getInstagramProfileKind(lead);
  if (kind) return kind === 'brand';

  return Boolean(fields(lead).instagramProfile);
}

/** A PERSONAL Instagram account, read for interests and tastes (FLOSEAI-625). */
export function isInterestsLead(lead: BrandLeadInput | null | undefined): boolean {
  return getInstagramProfileKind(lead) === 'person';
}

/**
 * The Instagram brand's category (e.g. "Marketing Agency", "Restaurant"), used
 * to label the brand niche when present.
 */
export function getBrandCategory(lead: BrandLeadInput | null | undefined): string | undefined {
  if (!lead) return undefined;
  const profile = fields(lead).instagramProfile as InstagramProfileFields | undefined;
  return sanitizeBusinessCategory(profile?.businessCategory);
}

/**
 * The brand bio comes from the Instagram biography, persisted into
 * `customFields.headline` by the enrichment pipeline.
 */
export function getBrandBio(lead: BrandLeadInput | null | undefined): string | undefined {
  if (!lead) return undefined;
  const headline = fields(lead).headline;
  return typeof headline === 'string' && headline.trim() ? headline : undefined;
}

/** Recurring themes the interests analysis identified on a personal account. */
export function getInterestTopics(lead: BrandLeadInput | null | undefined): string[] | undefined {
  if (!lead) return undefined;
  const topics = fields(lead).interestTopics;
  if (!Array.isArray(topics)) return undefined;

  const cleaned = topics.filter(
    (topic): topic is string => typeof topic === 'string' && topic.trim().length > 0,
  );
  return cleaned.length > 0 ? cleaned : undefined;
}
